import { type ModalComponentProps } from "../contexts/useModal";
import {
  Button,
  Checkbox,
  Fieldset,
  Flex,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Mark,
  Modal,
  NumberInput,
  PasswordInput,
  ScrollArea,
  Select,
  Table,
  Tabs,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { ReactNode, use, useEffect, useRef, useState } from "react";
import { Dropzone } from "@mantine/dropzone";
import {
  IconCancel,
  IconCheck,
  IconFile,
  IconFileExcel,
  IconKeyboard,
  IconLock,
  IconMessageCircle,
  IconMinus,
  IconPhoto,
  IconRefresh,
  IconRegistered,
  IconSettings,
  IconUpload,
  IconUser,
  IconUserCircle,
  IconX,
  IconChevronLeft,
  IconChevronRight,
  IconPackage,
} from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import "@mantine/dropzone/styles.css";
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";
import {
  boardDateFormat,
  INT_MAX,
  lpad,
  setActionLog,
  formatTerminalFirmwareType,
} from "../utils/util";
import { RoleList, RoleListPermissions } from "../types/types";
import dayjs from "dayjs";
import ExcelJS from "exceljs";

type TerminalModelRow = {
  terminalModelId: number;
  internalModelName: string;
  externalModelName: string;
  vanType: number;
  terminalColor?: string;
  useYn: string;
};

type RegisterSampleExportParams = {
  internalModelName: string;
  externalModelName: string;
  vanName: string;
  terminalColor: string;
  aliasName?: string;
  spSerialNoPrefix: string;
  spSerialNoDigitLength: number;
  spSerialNoStartNumber: number;
  productionQuantity: number;
  macPrefix: string;
  mac2StartNumber: number;
  serialNoReverse: boolean;
  macAddressReverse: boolean;
};

const normalizeMacPrefix = (macPrefix: string) =>
  macPrefix.replace(/\s+/g, "").toUpperCase();

/** S/N·MAC 고정문자열: 공백·특수문자 제거 후 대문자 */
const sanitizeAlphanumericPrefix = (value: string) =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

const sanitizeMacHexPrefix = (value: string) =>
  value
    .toUpperCase()
    .replace(/[^0-9A-F]/g, "")
    .slice(0, 6);

const normalizeModelName = (value: string) => value.trim().toUpperCase();

const formatMac1Display = (macPrefix: string) => {
  const normalized = normalizeMacPrefix(macPrefix);
  return normalized.match(/.{1,2}/g)?.join(" ") ?? normalized;
};

const buildMacAddress = (macPrefix: string, mac2Number: number) =>
  normalizeMacPrefix(macPrefix) +
  mac2Number.toString(16).toUpperCase().padStart(6, "0");

const buildMac3 = (mac2Number: number) =>
  mac2Number.toString(16).toUpperCase().padStart(6, "0");

const buildRegisterExcelBaseFilename = (params: RegisterSampleExportParams) => {
  const timestamp = dayjs().format("YYYYMMDD_HHmmss");
  return `${params.externalModelName}_${params.spSerialNoStartNumber}_${params.productionQuantity}_${timestamp}`;
};

const downloadRegisterSampleExcel = async (
  params: RegisterSampleExportParams,
  options?: { baseFilename?: string; duplicateIndex?: number },
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  worksheet.columns = [
    { header: "번호", key: "number", width: 8 },
    { header: "S/N", key: "spSerialNo", width: 18 },
    { header: "MAC", key: "macAddress", width: 18 },
    { header: "내부모델", key: "internalModelName", width: 14 },
    { header: "외부모델", key: "externalModelName", width: 14 },
    { header: "VAN", key: "vanName", width: 12 },
    { header: "색상", key: "terminalColor", width: 10 },
    { header: "MAC-1", key: "mac1", width: 12 },
    { header: "MAC-2", key: "mac2", width: 10 },
    { header: "MAC-3", key: "mac3", width: 10 },
    { header: "S/N 자리수", key: "spSerialNoLength", width: 10 },
    { header: "MAC 자리수", key: "macAddressLength", width: 10 },
  ];

  worksheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "76933C" },
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FF000000" } },
      bottom: { style: "thin", color: { argb: "FF000000" } },
      right: { style: "thin", color: { argb: "FF000000" } },
    };
  });

  const mac1Display = formatMac1Display(params.macPrefix);

  for (let i = 0; i < params.productionQuantity; i++) {
    const serialIndex = params.serialNoReverse
      ? params.productionQuantity - 1 - i
      : i;
    const macIndex = params.macAddressReverse
      ? params.productionQuantity - 1 - i
      : i;
    const spNumber = params.spSerialNoStartNumber + serialIndex;
    const mac2Number = params.mac2StartNumber + macIndex;
    const dataRow = worksheet.addRow({
      number: i + 1,
      spSerialNo:
        params.spSerialNoPrefix +
        lpad(String(spNumber), params.spSerialNoDigitLength, "0"),
      macAddress: buildMacAddress(params.macPrefix, mac2Number),
      internalModelName: params.internalModelName,
      externalModelName: params.externalModelName,
      vanName: params.vanName,
      terminalColor: params.terminalColor,
      mac1: mac1Display,
      mac2: mac2Number,
      mac3: buildMac3(mac2Number),
    });

    const rowNum = dataRow.number;
    dataRow.getCell(11).value = { formula: `LEN(B${rowNum})` };
    dataRow.getCell(12).value = { formula: `LEN(C${rowNum})` };

    const isStripedRow = i % 2 === 0;
    for (let col = 1; col <= 12; col++) {
      const cell = dataRow.getCell(col);
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin", color: { argb: "DDEBF7" } },
        left: { style: "thin", color: { argb: "DDEBF7" } },
        bottom: { style: "thin", color: { argb: "DDEBF7" } },
        right: { style: "thin", color: { argb: "DDEBF7" } },
      };
      if (isStripedRow) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEBF1DE" },
        };
      }
    }
  }

  const baseFilename =
    options?.baseFilename ?? buildRegisterExcelBaseFilename(params);
  const duplicateIndex = options?.duplicateIndex ?? 0;
  const filename =
    duplicateIndex > 0
      ? `${baseFilename} (${duplicateIndex}).xlsx`
      : `${baseFilename}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export type ErrorAlertProps = ModalComponentProps<boolean> & {
  content: string;
};
export type ConfirmProps = ModalComponentProps<boolean> & {
  title: string;
  content: string;
};
export type TerminalsExcelImportProps = ModalComponentProps<boolean> & {
  initialTab?: "excel" | "typing";
  initialMode?: "register" | "update" | "incoming";
};
export type WorkerUserGenerateProps = ModalComponentProps<boolean> & {};
export type WorkerUserRejectProps = ModalComponentProps<boolean> & {
  userNoList: number[];
  userData?: Array<{ userNo: number; userId: string; userName: string }>;
};
export type UserGenerateProps = ModalComponentProps<boolean> & {};
export type TerminalModelGenerateProps = ModalComponentProps<boolean> & {};
export type TerminalModelUpdateProps = ModalComponentProps<boolean> & {
  row: {
    terminalModelId: number;
    internalModelName: string;
    externalModelName: string;
    useYn: string;
  };
};
export type WorkerUserGenerateToAdminProps = ModalComponentProps<boolean> & {};
export type UserUpdateProps = ModalComponentProps<boolean> & {
  row: any;
  workerYn: string;
};
export type RoleAddProps = ModalComponentProps<boolean> & {};
export type RoleUpdateProps = ModalComponentProps<boolean> & {
  row: any;
};
export type PasswordChangeProps = ModalComponentProps<boolean> & {};
export type TerminalViewProps = ModalComponentProps<boolean> & {
  row: any;
};
export type ActionLogDetailProps = ModalComponentProps<boolean> & {
  row: any;
};
export type TerminalDeleteProps = ModalComponentProps<boolean> & {};
export type TerminalIncomingProps = ModalComponentProps<boolean> & {};
export type TerminalUpdateProps = ModalComponentProps<boolean> & {};

export function ErrorAlert({ content, onConfirm, onCancel }: ErrorAlertProps) {
  return (
    <Modal opened={true} onClose={onCancel} title={"Error"}>
      <div>{content}</div>
      <Flex justify={"flex-end"}>
        <button className="btn" onClick={onCancel}>
          확인
        </button>
      </Flex>
    </Modal>
  );
}

export function Confirm({ title, content, onConfirm, onCancel }: ConfirmProps) {
  return (
    <Modal opened={true} onClose={onCancel} title={title}>
      <Text size="md" pb={8}>
        {content}
      </Text>
      <Flex justify={"space-between"} align={"center"}>
        <Button
          leftSection={<IconX size={18} stroke={1.5} />}
          onClick={() => {
            onConfirm?.(false);
          }}
          variant="default"
        >
          취소
        </Button>

        <Button
          leftSection={<IconCheck size={18} stroke={1.5} />}
          onClick={() => {
            onConfirm?.(true);
          }}
          variant="gradient"
          gradient={{ from: "#e0eafc", to: "#b6e0fe", deg: 180 }}
          c={"#141414"}
        >
          확인
        </Button>
      </Flex>
    </Modal>
  );
}

export function TerminalsExcelImport({
  onConfirm,
  onCancel,
  initialTab = "typing",
  initialMode = "register",
}: TerminalsExcelImportProps) {
  const [mode, setMode] = useState<"register" | "update" | "incoming" | null>(
    initialMode,
  ); // 등록/수정/반품 모드 선택
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<string | null>(initialTab);
  const [isValidate, setIsValidate] = useState(false);

  const theme = useMantineTheme();
  const [terminalModelId, setTerminalModelId] = useState("");
  const [terminalModels, setTerminalModels] = useState<TerminalModelRow[]>([]);
  const [vanType, setVanType] = useState("");
  const [terminalColor, setTerminalColor] = useState("");
  const [vanTypeOptions, setVanTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [productionQuantity, setProductionQuantity] = useState<number | "">("");
  const [aliasName, setAliasName] = useState("");
  const [spSerialNoPrefix, setSpSerialNoPrefix] = useState("");
  const [spSerialNoDigitLength, setSpSerialNoDigitLength] = useState<
    number | ""
  >("");
  const [spSerialNoStartNumber, setSpSerialNoStartNumber] = useState<
    number | ""
  >("");
  const [macPrefix, setMacPrefix] = useState("");
  const [mac2StartNumber, setMac2StartNumber] = useState<number | "">("");
  const [serialNoReverse, setSerialNoReverse] = useState(false);
  const [macAddressReverse, setMacAddressReverse] = useState(false);
  const pendingRegisterExportRef = useRef<RegisterSampleExportParams | null>(
    null,
  );
  const pendingCreateBatchRequestRef = useRef<string | null>(null);
  const registerExcelBaseFilenameRef = useRef<string | null>(null);
  const registerExcelRedownloadCountRef = useRef(0);
  const submitCreateBatchLockRef = useRef(false);
  const [registerConfirmStep, setRegisterConfirmStep] = useState(false);
  const [registerConfirmKeyword, setRegisterConfirmKeyword] = useState("");
  const [registerPreviewParams, setRegisterPreviewParams] =
    useState<RegisterSampleExportParams | null>(null);

  const hasProductionQuantity =
    typeof productionQuantity === "number" && productionQuantity > 0;
  const hasSpDigitLength =
    typeof spSerialNoDigitLength === "number" && spSerialNoDigitLength > 0;
  const hasSpStartNumber = typeof spSerialNoStartNumber === "number";
  const hasMac2StartNumber = typeof mac2StartNumber === "number";

  const spEndNumber =
    hasProductionQuantity && hasSpStartNumber
      ? spSerialNoStartNumber + productionQuantity - 1
      : null;
  const mac2EndNumber =
    hasProductionQuantity && hasMac2StartNumber
      ? mac2StartNumber + productionQuantity - 1
      : null;

  const formatSpSerialPreview = (serialNumber: number | null) => {
    if (
      !spSerialNoPrefix.trim() ||
      !hasSpDigitLength ||
      serialNumber === null
    ) {
      return "-";
    }
    return (
      spSerialNoPrefix + lpad(String(serialNumber), spSerialNoDigitLength, "0")
    );
  };

  const formatMacPreview = (mac2: number | null) => {
    if (!macPrefix.trim() || mac2 === null) {
      return "-";
    }
    return buildMacAddress(macPrefix, mac2);
  };

  const terminalModelOptions = terminalModels.map((model) => ({
    value: String(model.terminalModelId),
    label: `${model.internalModelName} / ${model.externalModelName}`,
  }));

  const selectedTerminalModel = terminalModels.find(
    (model) => String(model.terminalModelId) === terminalModelId,
  );

  useEffect(() => {
    // 모드가 선택되어야 하고, 파일이나 정보가 입력되어야 함
    if (!mode) {
      setIsValidate(false);
      return;
    }

    if (tab === "excel" && excelFile) {
      setIsValidate(true);
    } else if (
      tab === "typing" &&
      terminalModelId !== "" &&
      hasProductionQuantity &&
      (mode !== "register" || aliasName.trim() !== "") &&
      spSerialNoPrefix.trim() !== "" &&
      hasSpDigitLength &&
      hasSpStartNumber &&
      macPrefix.length === 6 &&
      hasMac2StartNumber
    ) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [
    mode,
    tab,
    excelFile,
    terminalModelId,
    productionQuantity,
    aliasName,
    spSerialNoPrefix,
    spSerialNoDigitLength,
    spSerialNoStartNumber,
    macPrefix,
    mac2StartNumber,
  ]);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/terminal/model/list", {
        params: { currentPageNo: 1, recordsPerPage: INT_MAX },
      })
      .then((res) => {
        const list = (res.data?.data?.list ?? []).filter(
          (item: TerminalModelRow) =>
            String(item.useYn || "").toUpperCase() === "Y",
        );
        setTerminalModels(list);
      })
      .catch((err) => {
        console.log(err);
        setTerminalModels([]);
      });
    axiosInstance
      .get("/api/v1/common/van")
      .then((res) => {
        const mapped = res.data.data.map((item: any) => ({
          value: item.code,
          label: item.name,
        }));
        setVanTypeOptions(mapped);
      })
      .catch((err) => {
        console.log(err);
        setVanTypeOptions([]);
      });
  }, []);

  const syncModelDerivedFields = (nextId: string) => {
    if (!nextId) {
      setVanType("");
      setTerminalColor("");
      return;
    }
    const model = terminalModels.find(
      (item) => String(item.terminalModelId) === nextId,
    );
    if (!model) {
      setVanType("");
      setTerminalColor("");
      return;
    }
    const van = vanTypeOptions.find(
      (option) => option.value === String(model.vanType),
    );
    setVanType(van?.label || "");
    setTerminalColor(model.terminalColor || "");
  };

  const handleTerminalModelChange = (value: string | null) => {
    const nextId = value || "";
    setTerminalModelId(nextId);
    syncModelDerivedFields(nextId);
  };

  useEffect(() => {
    syncModelDerivedFields(terminalModelId);
  }, [terminalModelId, terminalModels, vanTypeOptions]);

  useEffect(() => {
    setRegisterConfirmStep(false);
    setRegisterConfirmKeyword("");
    setRegisterPreviewParams(null);
    pendingCreateBatchRequestRef.current = null;
    pendingRegisterExportRef.current = null;
    registerExcelBaseFilenameRef.current = null;
    registerExcelRedownloadCountRef.current = 0;
    if (mode !== "register") {
      setSerialNoReverse(false);
      setMacAddressReverse(false);
    }
  }, [tab, mode]);

  const buildTypingRequestPayload = (): {
    exportParams: RegisterSampleExportParams;
    requestJson: string;
  } | null => {
    if (
      !terminalModelId ||
      !hasProductionQuantity ||
      !hasSpDigitLength ||
      !hasSpStartNumber ||
      !hasMac2StartNumber ||
      !selectedTerminalModel
    ) {
      return null;
    }
    if (mode === "register" && !aliasName.trim()) {
      return null;
    }

    const normalizedSpPrefix = sanitizeAlphanumericPrefix(spSerialNoPrefix);
    const normalizedMacPrefix = sanitizeMacHexPrefix(macPrefix);
    const normalizedColor = terminalColor.trim().toUpperCase();
    const normalizedAlias = aliasName.trim().replace(/\s+/g, " ");
    const qty = productionQuantity as number;
    const digitLength = spSerialNoDigitLength as number;
    const spStart = spSerialNoStartNumber as number;
    const mac2Start = mac2StartNumber as number;

    return {
      exportParams: {
        internalModelName: selectedTerminalModel.internalModelName,
        externalModelName: selectedTerminalModel.externalModelName,
        vanName: vanType,
        terminalColor: normalizedColor,
        aliasName: normalizedAlias || undefined,
        spSerialNoPrefix: normalizedSpPrefix,
        spSerialNoDigitLength: digitLength,
        spSerialNoStartNumber: spStart,
        productionQuantity: qty,
        macPrefix: normalizedMacPrefix,
        mac2StartNumber: mac2Start,
        serialNoReverse,
        macAddressReverse,
      },
      requestJson: JSON.stringify({
        terminalModelId: Number(terminalModelId),
        terminalColor: normalizedColor,
        productionQuantity: qty,
        spSerialNoPrefix: normalizedSpPrefix,
        spSerialNoDigitLength: digitLength,
        spSerialNoStartNumber: spStart,
        macPrefix: normalizedMacPrefix,
        mac2StartNumber: mac2Start,
        serialNoReverse,
        macAddressReverse,
        aliasName: normalizedAlias || undefined,
        update: mode === "update",
        incoming: mode === "incoming",
      }),
    };
  };

  const submitCreateBatch = async (requestJson: string) => {
    if (submitCreateBatchLockRef.current) {
      return;
    }
    submitCreateBatchLockRef.current = true;
    const form = new FormData();
    form.append("request", requestJson);

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 정보 업로드 API호출",
      });
      const res = await axiosInstance.post(
        "/api/v1/terminal/create-batch",
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data.code === "200") {
        const groupId = res.data.data.terminalImportGroupId;
        try {
          localStorage.setItem("terminalImportGroupId", String(groupId));
        } catch {}
        const intervalId = setInterval(async () => {
          try {
            const progressResp = await axiosInstance.get(
              `/api/v1/terminal/imports/${groupId}`,
            );
            if (progressResp.data?.data.status === "FAILED") {
              const failMsg =
                progressResp.data?.data?.errorMessage ||
                "업로드에 실패했습니다.";
              throw new Error(failMsg);
            }

            const percent = progressResp.data?.data.progress ?? 1;
            setProgress(percent);
            if (percent >= 100) {
              try {
                localStorage.removeItem("terminalImportGroupId");
              } catch {}
              clearInterval(intervalId);
              setUploading(false);
              onConfirm?.(true);
            }
          } catch (err: unknown) {
            const failMsg =
              err instanceof Error ? err.message : "업로드에 실패했습니다.";
            toast.error(failMsg);
            setError(failMsg);
            await setActionLog({
              eventType: "IMPORT",
              title: "단말기 정보 업로드 실패",
              content: err,
            });
            try {
              localStorage.removeItem("terminalImportGroupId");
            } catch {}
            clearInterval(intervalId);
            setUploading(false);
            submitCreateBatchLockRef.current = false;
          }
        }, 500);
      } else {
        const msg = res.data?.msg || "등록 요청에 실패했습니다.";
        toast.error(msg);
        setError(msg);
        setUploading(false);
        submitCreateBatchLockRef.current = false;
      }
    } catch (e: any) {
      const msg =
        e.response?.data?.message ||
        e.message ||
        "업로드 중 오류가 발생했습니다.";
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 정보 업로드 실패",
        content: msg,
      });
      toast.error(msg);
      setError(msg);
      setUploading(false);
      submitCreateBatchLockRef.current = false;
    }
  };

  const handleRegisterPreview = async () => {
    const payload = buildTypingRequestPayload();
    if (!payload) {
      toast.error("모델과 생산 정보를 모두 입력해주세요.");
      return;
    }

    const baseFilename = buildRegisterExcelBaseFilename(payload.exportParams);
    registerExcelBaseFilenameRef.current = baseFilename;
    registerExcelRedownloadCountRef.current = 0;

    try {
      await downloadRegisterSampleExcel(payload.exportParams, { baseFilename });
    } catch (exportErr) {
      console.error(exportErr);
      toast.error("등록 엑셀 다운로드에 실패하였습니다.");
      return;
    }

    pendingRegisterExportRef.current = payload.exportParams;
    pendingCreateBatchRequestRef.current = payload.requestJson;
    setRegisterConfirmKeyword("");
    setRegisterPreviewParams(payload.exportParams);
    setRegisterConfirmStep(true);
    setError(null);
  };

  const handleConfirmRegister = async () => {
    if (submitCreateBatchLockRef.current || uploading) {
      return;
    }
    const requestJson = pendingCreateBatchRequestRef.current;
    if (!requestJson) {
      toast.error("등록 정보가 없습니다. 다시 시도해주세요.");
      setRegisterConfirmStep(false);
      return;
    }

    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 직접입력 등록 확인",
    });
    await submitCreateBatch(requestJson);
  };

  const handleCancelRegisterConfirm = () => {
    setRegisterConfirmStep(false);
    setRegisterConfirmKeyword("");
    setRegisterPreviewParams(null);
    pendingCreateBatchRequestRef.current = null;
    pendingRegisterExportRef.current = null;
    registerExcelBaseFilenameRef.current = null;
    registerExcelRedownloadCountRef.current = 0;
    setError(null);
  };

  const handleRedownloadRegisterExcel = async () => {
    const exportParams = pendingRegisterExportRef.current;
    const baseFilename = registerExcelBaseFilenameRef.current;
    if (!exportParams || !baseFilename) {
      return;
    }
    registerExcelRedownloadCountRef.current += 1;
    try {
      await downloadRegisterSampleExcel(exportParams, {
        baseFilename,
        duplicateIndex: registerExcelRedownloadCountRef.current,
      });
    } catch (exportErr) {
      console.error(exportErr);
      toast.error("등록 엑셀 다운로드에 실패하였습니다.");
      registerExcelRedownloadCountRef.current -= 1;
    }
  };

  const handleExcelDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (
      file.type === "application/vnd.ms-excel" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setExcelFile(file);
      setError(null);
      toast.dismiss();
    } else {
      const msg = "엑셀 파일만 업로드 가능합니다.";
      setError(msg);
      toast.error(msg);
      setExcelFile(null);
    }
  };

  const handleUpload = async () => {
    if (!mode) {
      toast.error("등록 또는 수정, 반품 모드를 선택해주세요.");
      return;
    }

    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기정보 업로드 모달창/업로드 버튼 클릭",
    });

    if (tab === "typing" && mode === "register") {
      await handleRegisterPreview();
      return;
    }

    const form = new FormData();
    let url = "/api/v1/terminal";

    if (tab === "excel") {
      if (!excelFile) {
        const msg = "엑셀 파일을 업로드 해주세요.";
        toast.error(msg);
        return;
      }
      form.append("excelFile", excelFile);
      form.append("update", mode === "update" ? "true" : "false");
      form.append("incoming", mode === "incoming" ? "true" : "false");
      url += "/imports";
    } else {
      const payload = buildTypingRequestPayload();
      if (!payload) {
        toast.error("모델과 생산 정보를 모두 입력해주세요.");
        return;
      }
      await submitCreateBatch(payload.requestJson);
      return;
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 정보 업로드 API호출",
      });
      const res = await axiosInstance.post(url, form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.code === "200") {
        const groupId = res.data.data.terminalImportGroupId;
        try {
          localStorage.setItem("terminalImportGroupId", String(groupId));
        } catch {}
        const intervalId = setInterval(async () => {
          try {
            const progressResp = await axiosInstance.get(
              `/api/v1/terminal/imports/${groupId}`,
            );
            if (progressResp.data?.data.status === "FAILED") {
              const failMsg =
                progressResp.data?.data?.errorMessage ||
                "업로드에 실패했습니다.";
              throw new Error(failMsg);
            }

            const percent = progressResp.data?.data.progress ?? 1;
            setProgress(percent);
            if (percent >= 100) {
              try {
                localStorage.removeItem("terminalImportGroupId");
              } catch {}
              clearInterval(intervalId);
              setUploading(false);
              submitCreateBatchLockRef.current = false;
              onConfirm?.(true);
            }
          } catch (err: unknown) {
            const failMsg =
              err instanceof Error ? err.message : "업로드에 실패했습니다.";
            toast.error(failMsg);
            setError(failMsg);
            await setActionLog({
              eventType: "IMPORT",
              title: "단말기 정보 업로드 실패",
              content: err,
            });
            try {
              localStorage.removeItem("terminalImportGroupId");
            } catch {}
            clearInterval(intervalId);
            setUploading(false);
            submitCreateBatchLockRef.current = false;
          }
        }, 500);
      } else {
        const msg = res.data?.msg || "업로드 요청에 실패했습니다.";
        toast.error(msg);
        setError(msg);
        setUploading(false);
        submitCreateBatchLockRef.current = false;
      }
    } catch (e: any) {
      const msg =
        e.response?.data?.message ||
        e.message ||
        "업로드 중 오류가 발생했습니다.";
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 정보 업로드 실패",
        content: msg,
      });
      toast.error(msg);
      setError(msg);
      setUploading(false);
      submitCreateBatchLockRef.current = false;
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={registerConfirmStep ? "등록 확인" : "단말기 등록/수정"}
      size={registerConfirmStep || tab !== "excel" ? "xl" : "md"}
    >
      {registerConfirmStep && registerPreviewParams ? (
        <>
          <Text mb="md">
            등록 엑셀이 다운로드되었습니다. 내용을 확인한 후 등록을
            진행해주세요.
          </Text>
          <Fieldset legend="등록 요약" mb="md">
            <Text size="sm">
              모델: {registerPreviewParams.internalModelName} /{" "}
              {registerPreviewParams.externalModelName}
            </Text>
            <Text size="sm">VAN: {registerPreviewParams.vanName}</Text>
            <Text size="sm">색상: {registerPreviewParams.terminalColor}</Text>
            {registerPreviewParams.aliasName && (
              <Text size="sm">별명: {registerPreviewParams.aliasName}</Text>
            )}
            <Text size="sm">
              생산 대수: {registerPreviewParams.productionQuantity}대
            </Text>
            <Text size="sm">
              S/N:{" "}
              {registerPreviewParams.serialNoReverse
                ? `${registerPreviewParams.spSerialNoPrefix}${lpad(
                    String(
                      registerPreviewParams.spSerialNoStartNumber +
                        registerPreviewParams.productionQuantity -
                        1,
                    ),
                    registerPreviewParams.spSerialNoDigitLength,
                    "0",
                  )} ~ ${registerPreviewParams.spSerialNoPrefix}${lpad(
                    String(registerPreviewParams.spSerialNoStartNumber),
                    registerPreviewParams.spSerialNoDigitLength,
                    "0",
                  )} (역순)`
                : `${registerPreviewParams.spSerialNoPrefix}${lpad(
                    String(registerPreviewParams.spSerialNoStartNumber),
                    registerPreviewParams.spSerialNoDigitLength,
                    "0",
                  )} ~ ${registerPreviewParams.spSerialNoPrefix}${lpad(
                    String(
                      registerPreviewParams.spSerialNoStartNumber +
                        registerPreviewParams.productionQuantity -
                        1,
                    ),
                    registerPreviewParams.spSerialNoDigitLength,
                    "0",
                  )}`}
            </Text>
            <Text size="sm">
              MAC:{" "}
              {registerPreviewParams.macAddressReverse
                ? `${buildMacAddress(
                    registerPreviewParams.macPrefix,
                    registerPreviewParams.mac2StartNumber +
                      registerPreviewParams.productionQuantity -
                      1,
                  )} ~ ${buildMacAddress(
                    registerPreviewParams.macPrefix,
                    registerPreviewParams.mac2StartNumber,
                  )} (역순)`
                : `${buildMacAddress(
                    registerPreviewParams.macPrefix,
                    registerPreviewParams.mac2StartNumber,
                  )} ~ ${buildMacAddress(
                    registerPreviewParams.macPrefix,
                    registerPreviewParams.mac2StartNumber +
                      registerPreviewParams.productionQuantity -
                      1,
                  )}`}
            </Text>
          </Fieldset>
          <Button
            variant="light"
            leftSection={<IconFileExcel size={16} />}
            onClick={handleRedownloadRegisterExcel}
            mb="md"
            disabled={uploading}
          >
            엑셀 다시 받기
          </Button>
        </>
      ) : (
        <>
          <Text mb={8}>단말기 정보를 등록하거나 수정합니다.</Text>

          {/* 등록/수정 모드 선택 */}
          <Group mb="md" gap="xs">
            <Button
              variant={mode === "register" ? "filled" : "outline"}
              style={
                mode === "register"
                  ? { backgroundColor: "#81B29A", color: "#fff" }
                  : { borderColor: "#81B29A", color: "#81B29A" }
              }
              onClick={() => setMode("register")}
              size="sm"
            >
              등록
            </Button>
            {/* <Button
              variant={mode === "update" ? "filled" : "outline"}
              style={
                mode === "update"
                  ? { backgroundColor: "#5B8FA8", color: "#fff" }
                  : { borderColor: "#5B8FA8", color: "#5B8FA8" }
              }
              onClick={() => setMode("update")}
              size="sm"
            >
              수정
            </Button>
            <Button
              variant={mode === "incoming" ? "filled" : "outline"}
              style={
                mode === "incoming"
                  ? { backgroundColor: "#a85b5b", color: "#fff" }
                  : { borderColor: "#a85b5b", color: "#a85b5b" }
              }
              onClick={() => setMode("incoming")}
              size="sm"
            >
              반품
            </Button> */}
          </Group>

          <Tabs defaultValue={tab} onChange={setTab}>
            <Tabs.List>
              <Tabs.Tab value="excel" leftSection={<IconFileExcel size={12} />}>
                엑셀
              </Tabs.Tab>
              <Tabs.Tab value="typing" leftSection={<IconKeyboard size={12} />}>
                직접입력
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="excel" pt={"1rem"}>
              <Dropzone
                mb={16}
                multiple={false}
                onDrop={handleExcelDrop}
                maxSize={10 * 1024 ** 2}
                accept={[
                  "application/vnd.ms-excel",
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                ]}
                disabled={uploading}
              >
                <Group
                  justify="center"
                  gap="xl"
                  mih={80}
                  style={{ pointerEvents: "none" }}
                >
                  <Text size="lg">
                    {excelFile
                      ? `${excelFile.name} (${Math.round(
                          excelFile.size / 1024,
                        )} KB)`
                      : "엑셀 파일을 드래그하거나 클릭해서 업로드"}
                  </Text>
                </Group>
              </Dropzone>
            </Tabs.Panel>
            <Tabs.Panel value="typing" pt={"1rem"}>
              <>
                <Grid pb={"0.5rem"}>
                  <Grid.Col span={4}>
                    <Select
                      label="모델"
                      placeholder="사용 중인 단말기 모델 선택"
                      data={terminalModelOptions}
                      value={terminalModelId || null}
                      onChange={handleTerminalModelChange}
                      clearable={false}
                      allowDeselect={false}
                      searchable
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      label="VAN"
                      value={vanType}
                      readOnly
                      placeholder="모델 선택 시 자동 설정"
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      label="색상"
                      value={terminalColor}
                      readOnly
                      placeholder="모델 선택 시 자동 설정"
                    />
                  </Grid.Col>
                </Grid>
                <Grid pb={"0.5rem"}>
                  <Grid.Col span={4}>
                    <NumberInput
                      label="생산 대수"
                      value={productionQuantity}
                      min={1}
                      onChange={(value) =>
                        setProductionQuantity(
                          typeof value === "number" ? value : "",
                        )
                      }
                    />
                  </Grid.Col>
                  {mode === "register" && (
                    <Grid.Col span={8}>
                      <TextInput
                        label="별명"
                        placeholder="예: 6월생산1차 (동일 별명은 같은 그룹)"
                        value={aliasName}
                        maxLength={50}
                        onChange={(e) => setAliasName(e.currentTarget.value)}
                      />
                    </Grid.Col>
                  )}
                </Grid>
                <Grid pb={"0.5rem"}>
                  <Grid.Col span={6}>
                    <Fieldset legend="S/N" pb={"0.5rem"}>
                      {mode === "register" && (
                        <Checkbox
                          label="S/N 역순"
                          checked={serialNoReverse}
                          onChange={(e) =>
                            setSerialNoReverse(e.currentTarget.checked)
                          }
                          mb="sm"
                        />
                      )}
                      <Grid pb={"0.5rem"}>
                        <Grid.Col span={6}>
                          <TextInput
                            label="고정문자열"
                            placeholder="예: PD2606"
                            value={spSerialNoPrefix}
                            onChange={(e) => {
                              setSpSerialNoPrefix(
                                sanitizeAlphanumericPrefix(e.target.value),
                              );
                            }}
                          />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <NumberInput
                            label="자릿수"
                            value={spSerialNoDigitLength}
                            min={1}
                            onChange={(value) =>
                              setSpSerialNoDigitLength(
                                typeof value === "number" ? value : "",
                              )
                            }
                          />
                        </Grid.Col>
                      </Grid>
                      <Grid pb={"0.5rem"}>
                        <Grid.Col span={12}>
                          <NumberInput
                            label="시작번호"
                            value={spSerialNoStartNumber}
                            min={0}
                            onChange={(value) =>
                              setSpSerialNoStartNumber(
                                typeof value === "number" ? value : "",
                              )
                            }
                          />
                        </Grid.Col>
                      </Grid>
                      <Text>{`시작 : ${formatSpSerialPreview(
                        hasSpStartNumber ? spSerialNoStartNumber : null,
                      )}`}</Text>
                      <Text>{`종료 : ${formatSpSerialPreview(spEndNumber)}`}</Text>
                      <Text>{`총 : ${hasProductionQuantity ? productionQuantity : "-"}개`}</Text>
                      {mode === "register" && serialNoReverse && (
                        <Text size="sm" c="blue">
                          등록 순서: 종료 S/N부터 시작 S/N 순으로 등록됩니다.
                        </Text>
                      )}
                    </Fieldset>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Fieldset legend="MAC address" pb={"0.5rem"}>
                      {mode === "register" && (
                        <Checkbox
                          label="MAC 역순"
                          checked={macAddressReverse}
                          onChange={(e) =>
                            setMacAddressReverse(e.currentTarget.checked)
                          }
                          mb="sm"
                        />
                      )}
                      <Grid pb={"0.5rem"}>
                        <Grid.Col span={12}>
                          <TextInput
                            label="MAC-1 (고정문자열)"
                            placeholder="예: C4E7BE"
                            maxLength={6}
                            value={macPrefix}
                            onChange={(e) => {
                              setMacPrefix(
                                sanitizeMacHexPrefix(e.target.value),
                              );
                            }}
                          />
                        </Grid.Col>
                      </Grid>
                      <Grid pb={"0.5rem"}>
                        <Grid.Col span={6}>
                          <NumberInput
                            label="MAC-2 시작번호"
                            value={mac2StartNumber}
                            min={0}
                            onChange={(value) =>
                              setMac2StartNumber(
                                typeof value === "number" ? value : "",
                              )
                            }
                          />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <NumberInput
                            label="MAC-2 종료번호"
                            value={mac2EndNumber ?? ""}
                            readOnly
                          />
                        </Grid.Col>
                      </Grid>
                      <Text>{`시작 MAC : ${formatMacPreview(
                        hasMac2StartNumber ? mac2StartNumber : null,
                      )}`}</Text>
                      <Text>{`종료 MAC : ${formatMacPreview(mac2EndNumber)}`}</Text>
                      <Text>{`총 : ${hasProductionQuantity ? productionQuantity : "-"}개`}</Text>
                      {mode === "register" && macAddressReverse && (
                        <Text size="sm" c="violet">
                          등록 순서: 종료 MAC부터 시작 MAC 순으로 등록됩니다.
                        </Text>
                      )}
                      <Text size="xs" c="dimmed" mt={4}>
                        MAC-2 번호는 10진수이며, MAC-3(6자리 hex)로 변환되어 MAC
                        주소가 생성됩니다.
                      </Text>
                    </Fieldset>
                  </Grid.Col>
                </Grid>
              </>
            </Tabs.Panel>
          </Tabs>
        </>
      )}

      {uploading && (
        <Group gap="xs" mb="xs">
          <Loader size="sm" />
          <Text size="sm">업로드 중... {progress}%</Text>
        </Group>
      )}

      {error && (
        <Text size="sm" c="red" mb="xs">
          {error}
        </Text>
      )}

      {registerConfirmStep ? (
        <Flex justify="space-between" align="center">
          <Button
            variant="default"
            leftSection={<IconX size={20} stroke={1.5} />}
            onClick={handleCancelRegisterConfirm}
            disabled={uploading}
          >
            취소
          </Button>
          <Group align="center" gap="sm" justify="flex-end" wrap="nowrap">
            <Text size="sm" style={{ whiteSpace: "nowrap" }}>
              아래 입력란에 「OK」를 입력한 후 확인 버튼을 클릭해주세요.
            </Text>
            <TextInput
              placeholder="OK"
              value={registerConfirmKeyword}
              onChange={(e) =>
                setRegisterConfirmKeyword(e.target.value.toUpperCase())
              }
              disabled={uploading}
              w={100}
            />
            <Button
              variant="gradient"
              gradient={{ from: "#e0eafc", to: "#b6e0fe", deg: 180 }}
              c="#141414"
              leftSection={<IconCheck size={20} stroke={1.5} />}
              onClick={handleConfirmRegister}
              loading={uploading}
              disabled={registerConfirmKeyword.trim() !== "OK" || uploading}
            >
              {uploading ? "등록 중..." : "확인"}
            </Button>
          </Group>
        </Flex>
      ) : (
        <Flex justify="space-between">
          <Button
            variant="filled"
            color={mode === "update" ? "blue" : "teal"}
            leftSection={<IconUpload size={20} stroke={1.5} />}
            onClick={handleUpload}
            loading={uploading}
            disabled={!isValidate || uploading}
          >
            {uploading
              ? mode === "update"
                ? "수정 중..."
                : mode === "incoming"
                  ? "반품 중..."
                  : "등록 중..."
              : mode === "update"
                ? "수정"
                : mode === "incoming"
                  ? "반품"
                  : "등록"}
          </Button>
          <Button
            variant="default"
            color="gray"
            leftSection={<IconX size={20} stroke={1.5} />}
            onClick={onCancel}
          >
            취소
          </Button>
        </Flex>
      )}
    </Modal>
  );
}

export function WorkerUserGenerate({
  onConfirm,
  onCancel,
}: WorkerUserGenerateProps) {
  const [userId, setUserId] = useState("");
  const [isUniqueuserId, setIsUniqueuserId] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [userIdError, setUserIdError] = useState<string | null>(null);
  const [password2Error, setPassword2Error] = useState<string | null>(null);
  const [userNameError, setUserNameError] = useState<string | null>(null);

  useEffect(() => {
    if (isUniqueuserId && password && userName) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [isUniqueuserId, password, userName]);

  useEffect(() => {
    if (password === password2) {
      setPassword2Error(null);
    } else {
      setPassword2Error("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
    }
  }, [password2]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const validateUserId = async (value: string) => {
    setIsUniqueuserId(false);
    try {
      const res = await axiosInstance.get(`/api/v1/user/check/${value}`);
      if (res.data.code === "200") {
        setIsUniqueuserId(true);
      } else {
        setUserIdError("이미 사용중인 아이디 입니다.");
      }
    } catch (err) {
      setUserIdError("이미 사용중인 아이디 입니다.");
      setIsValidate(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUserId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUniqueuserId(false);
    setUserIdError(null);
    const value = e.target.value;
    setUserId(value);

    if (value.length < 3) {
      setUserIdError("아이디는 최소 3글자 이상이여야합니다.");
    } else {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        validateUserId(value);
      }, 500);
    }
  };

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserName(value);

    if (value.length < 2) {
      setUserNameError("이름은 최소 2글자 이상이여야합니다.");
    } else {
      setUserNameError(null);
    }
  };

  const handleUserRegist = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/v1/user/worker", {
        userId: userId,
        password: password,
        userName: userName,
        workerYn: "Y",
      });
      if (res.data.code === "200") {
        toast.success("작업자 등록 성공");
        onConfirm?.(true);
      } else {
        toast.error(res.data.msg || "작업자 등록 실패");
      }
    } catch (err) {
      toast.error("작업자 등록 중 오류 발생");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"작업자 등록"}
      closeOnClickOutside={false}
    >
      <TextInput
        label="아이디"
        mt="md"
        size="lg"
        placeholder="아이디"
        withAsterisk
        value={userId}
        onChange={handleUserId}
        disabled={loading}
        error={userIdError}
      />
      <PasswordInput
        label="비밀번호"
        mt="md"
        size="lg"
        placeholder="비밀번호"
        withAsterisk
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="비밀번호 확인"
        mt="md"
        size="lg"
        placeholder="비밀번호 확인"
        withAsterisk
        value={password2}
        onChange={(e) => {
          setPassword2(e.target.value);
        }}
        disabled={loading}
        error={password2Error}
      />
      <TextInput
        label="이름"
        mt="md"
        size="lg"
        placeholder="이름"
        withAsterisk
        value={userName}
        onChange={handleUserName}
        disabled={loading}
        error={userNameError}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        size="lg"
        color="#222b45"
        leftSection={<IconUserCircle size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserRegist}
        disabled={!isValidate}
      >
        등록
      </Button>
    </Modal>
  );
}

export function WorkerUserReject({
  userNoList,
  userData,
  onConfirm,
  onCancel,
}: WorkerUserRejectProps) {
  const [rejectText, setRejectText] = useState("");
  const [rejectTextError, setRejectTextError] = useState<string | null>(null);
  const theme = useMantineTheme();

  const handleReject = async () => {
    if (rejectText.trim() === "") {
      setRejectTextError("반려사유를 입력해주세요.");
      return;
    }

    try {
      const res = await axiosInstance.delete("/api/v1/user/delete", {
        data: userNoList,
      });
      if (res.data.code === "200") {
        await setActionLog({
          eventType: "DELETE",
          title: "작업자 계정 반려 완료",
          content: { rejectText, userNoList, userData },
        });
        toast.success("반려 작업이 완료되었습니다.");
        onConfirm?.(true);
      } else {
        await setActionLog({
          eventType: "DELETE",
          title: "작업자 계정 반려 실패",
          content: { rejectText, userNoList, userData },
        });
        toast.error("반려 작업에 실패하였습니다.");
      }
    } catch (err) {
      await setActionLog({
        eventType: "DELETE",
        title: "작업자 계정 반려 실패",
        content: { err, userNoList, userData },
      });
      toast.error("반려 작업에 실패하였습니다.");
    }
  };
  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"작업자 반려"}
      closeOnClickOutside={false}
    >
      <Text size="md" pb={8}>
        총 {userNoList.length}명의 작업자를{" "}
        <Text span c="red" inherit>
          반려
        </Text>
        처리 하시겠습니까?
      </Text>
      <Text size="sm" pb={16} c={"red"}>
        반려 시 반려사유를 작성해야 합니다.
      </Text>
      <TextInput
        pb={16}
        label="반려사유"
        placeholder="반려사유를 입력해주세요."
        value={rejectText}
        onChange={(e) => {
          setRejectText(e.target.value);
        }}
        error={rejectTextError}
      />
      <Flex>
        <Button
          mr={"auto"}
          variant="filled"
          color="red"
          leftSection={
            <IconMinus size={20} color={theme.colors.blue[0]} stroke={1.5} />
          }
          onClick={handleReject}
        >
          반려
        </Button>
        <Button
          variant="default"
          onClick={onCancel}
          leftSection={
            <IconX size={20} color={theme.colors.dark[9]} stroke={1.5} />
          }
        >
          취소
        </Button>
      </Flex>
    </Modal>
  );
}

export function WorkerUserGenerateToAdmin({
  onConfirm,
  onCancel,
}: WorkerUserGenerateToAdminProps) {
  const [userId, setUserId] = useState("");
  const [isUniqueuserId, setIsUniqueuserId] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [roleCodeOptions, setRoleCodeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [roleCode, setRoleCode] = useState("");
  // const [orgOptions, setOrgOptions] = useState<
  //   { value: string; label: string }[]
  // >([]);
  // const [orgCode, setOrgCode] = useState("");

  const [userIdError, setUserIdError] = useState<string | null>(null);
  const [password2Error, setPassword2Error] = useState<string | null>(null);
  const [userNameError, setUserNameError] = useState<string | null>(null);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/role/permission/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          workerYn: "Y",
        },
      })
      .then((res) => {
        const mapped = res.data.data.list.map((item: any) => ({
          value: item.roles,
          label: item.roleName,
        }));

        const withDefault = [{ value: "", label: "권한선택" }, ...mapped];
        setRoleCodeOptions(withDefault);
      })
      .catch((err) => {
        console.log(err);
        setRoleCodeOptions([{ value: "", label: "권한선택" }]);
      });
  }, []);

  // useEffect(() => {
  //   const roleBased = roleCodeOptions.filter((opt) => opt.value !== "");
  //   const withDefault = [
  //     { value: "", label: "조직선택" },
  //     { value: "AA", label: "AA" },
  //     { value: "BB", label: "BB" },
  //     ...roleBased,
  //   ];
  //   setOrgOptions(withDefault);
  // }, [roleCodeOptions]);

  useEffect(() => {
    if (isUniqueuserId && password && userName && roleCode) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [isUniqueuserId, password, userName, roleCode]);

  useEffect(() => {
    if (password === password2) {
      setPassword2Error(null);
    } else {
      setPassword2Error("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
    }
  }, [password2]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const validateUserId = async (value: string) => {
    setIsUniqueuserId(false);
    try {
      const res = await axiosInstance.get(`/api/v1/user/check/${value}`);
      if (res.data.code === "200") {
        setIsUniqueuserId(true);
      } else {
        setUserIdError("이미 사용중인 아이디 입니다.");
      }
    } catch (err) {
      setUserIdError("이미 사용중인 아이디 입니다.");
      setIsValidate(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUserId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUniqueuserId(false);
    setUserIdError(null);
    const value = e.target.value;
    setUserId(value);

    if (value.length < 3) {
      setUserIdError("아이디는 최소 3글자 이상이여야합니다.");
    } else {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        validateUserId(value);
      }, 500);
    }
  };

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserName(value);

    if (value.length < 2) {
      setUserNameError("이름은 최소 2글자 이상이여야합니다.");
    } else {
      setUserNameError(null);
    }
  };

  const handleUserRegist = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/v1/user", {
        userId: userId,
        password: password,
        userName: userName,
        roleCode: roleCode,
        // orgCode: orgCode,
        workerYn: "Y",
      });
      if (res.data.code === "200") {
        await setActionLog({
          eventType: "CREATE",
          title: "작업자 계정 등록 성공",
          content: JSON.stringify({ userId, userName, roleCode }),
        });
        onConfirm?.(true);
      } else {
        toast.error(res.data.msg || "작업자 계정등록 실패");
        await setActionLog({
          eventType: "CREATE",
          title: "작업자 계정 등록 실패",
          content: JSON.stringify({ userId, userName, roleCode }),
        });
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "CREATE",
        title: "작업자 계정 등록 실패",
        content: err,
      });
      toast.error("작업자 계정등록 중 오류 발생");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"유저 등록"}
      closeOnClickOutside={false}
    >
      <TextInput
        label="아이디"
        mt="md"
        size="lg"
        placeholder="아이디"
        withAsterisk
        value={userId}
        onChange={handleUserId}
        disabled={loading}
        error={userIdError}
      />
      <PasswordInput
        label="비밀번호"
        mt="md"
        size="lg"
        placeholder="비밀번호"
        withAsterisk
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="비밀번호 확인"
        mt="md"
        size="lg"
        placeholder="비밀번호 확인"
        withAsterisk
        value={password2}
        onChange={(e) => {
          setPassword2(e.target.value);
        }}
        disabled={loading}
        error={password2Error}
      />
      <TextInput
        label="이름"
        mt="md"
        size="lg"
        placeholder="이름"
        withAsterisk
        value={userName}
        onChange={handleUserName}
        disabled={loading}
        error={userNameError}
      />
      {/*
      <Select
        label="조직"
        mt="md"
        size="lg"
        withAsterisk
        data={orgOptions}
        defaultValue={orgCode}
        onChange={(_value) => setOrgCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      */}
      <Select
        label="권한"
        mt="md"
        size="lg"
        withAsterisk
        data={roleCodeOptions}
        defaultValue={roleCode}
        onChange={(_value, option) => setRoleCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        size="lg"
        color="#222b45"
        leftSection={<IconUserCircle size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserRegist}
        disabled={!isValidate}
      >
        등록
      </Button>
    </Modal>
  );
}

export function UserGenerate({ onConfirm, onCancel }: UserGenerateProps) {
  const [userId, setUserId] = useState("");
  const [isUniqueuserId, setIsUniqueuserId] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [roleCodeOptions, setRoleCodeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [roleCode, setRoleCode] = useState("");
  // const [orgOptions, setOrgOptions] = useState<
  //   { value: string; label: string }[]
  // >([]);
  // const [orgCode, setOrgCode] = useState("");
  const [userIdError, setUserIdError] = useState<string | null>(null);
  const [password2Error, setPassword2Error] = useState<string | null>(null);
  const [userNameError, setUserNameError] = useState<string | null>(null);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/role/permission/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          workerYn: "N",
        },
      })
      .then((res) => {
        const mapped = res.data.data.list.map((item: any) => ({
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          value: item.roles,
          label: item.roleName,
        }));

        const withDefault = [{ value: "", label: "권한선택" }, ...mapped];
        setRoleCodeOptions(withDefault);
      })
      .catch((err) => {
        console.log(err);
        setRoleCodeOptions([{ value: "", label: "권한선택" }]);
      });
  }, []);

  // useEffect(() => {
  //   const roleBased = roleCodeOptions.filter((opt) => opt.value !== "");
  //   const withDefault = [
  //     { value: "", label: "조직선택" },
  //     { value: "AA", label: "AA" },
  //     { value: "BB", label: "BB" },
  //     ...roleBased,
  //   ];
  //   setOrgOptions(withDefault);
  // }, [roleCodeOptions]);

  useEffect(() => {
    if (isUniqueuserId && password && userName && roleCode) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [isUniqueuserId, password, userName, roleCode]);

  useEffect(() => {
    if (password === password2) {
      setPassword2Error(null);
    } else {
      setPassword2Error("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
    }
  }, [password2]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const validateUserId = async (value: string) => {
    setIsUniqueuserId(false);
    try {
      const res = await axiosInstance.get(`/api/v1/user/check/${value}`);
      if (res.data.code === "200") {
        setIsUniqueuserId(true);
      } else {
        setUserIdError("이미 사용중인 아이디 입니다.");
      }
    } catch (err) {
      setUserIdError("이미 사용중인 아이디 입니다.");
      setIsValidate(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUserId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUniqueuserId(false);
    setUserIdError(null);
    const value = e.target.value;
    setUserId(value);

    if (value.length < 3) {
      setUserIdError("아이디는 최소 3글자 이상이여야합니다.");
    } else {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        validateUserId(value);
      }, 500);
    }
  };

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserName(value);

    if (value.length < 2) {
      setUserNameError("이름은 최소 2글자 이상이여야합니다.");
    } else {
      setUserNameError(null);
    }
  };

  const handleUserRegist = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/v1/user", {
        userId: userId,
        password: password,
        userName: userName,
        roleCode: roleCode,
        workerYn: "N",
      });
      if (res.data.code === "200") {
        await setActionLog({
          eventType: "CREATE",
          title: "유저등록 성공",
          content: JSON.stringify({ userId, userName, roleCode }),
        });
        onConfirm?.(true);
      } else {
        await setActionLog({
          eventType: "CREATE",
          title: "유저등록 실패",
          content: JSON.stringify({ userId, userName, roleCode }),
        });
        toast.error(res.data.msg || "유저등록 실패");
      }
    } catch (err) {
      await setActionLog({
        eventType: "CREATE",
        title: "유저등록 실패",
        content: err,
      });
      toast.error("유저등록 중 오류 발생");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"유저 등록"}
      closeOnClickOutside={false}
    >
      <TextInput
        label="아이디"
        mt="md"
        size="lg"
        placeholder="아이디"
        withAsterisk
        value={userId}
        onChange={handleUserId}
        disabled={loading}
        error={userIdError}
      />
      <PasswordInput
        label="비밀번호"
        mt="md"
        size="lg"
        placeholder="비밀번호"
        withAsterisk
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="비밀번호 확인"
        mt="md"
        size="lg"
        placeholder="비밀번호 확인"
        withAsterisk
        value={password2}
        onChange={(e) => {
          setPassword2(e.target.value);
        }}
        disabled={loading}
        error={password2Error}
      />
      <TextInput
        label="이름"
        mt="md"
        size="lg"
        placeholder="이름"
        withAsterisk
        value={userName}
        onChange={handleUserName}
        disabled={loading}
        error={userNameError}
      />
      {/*
      <Select
        label="조직"
        mt="md"
        size="lg"
        withAsterisk
        data={orgOptions}
        defaultValue={orgCode}
        onChange={(_value) => setOrgCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      */}
      <Select
        label="권한"
        mt="md"
        size="lg"
        withAsterisk
        data={roleCodeOptions}
        defaultValue={roleCode}
        onChange={(_value, option) => setRoleCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        size="lg"
        color="#222b45"
        leftSection={<IconUserCircle size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserRegist}
        disabled={!isValidate}
      >
        등록
      </Button>
    </Modal>
  );
}

export function TerminalModelGenerate({
  onConfirm,
  onCancel,
}: TerminalModelGenerateProps) {
  const [internalModelName, setInternalModelName] = useState("");
  const [externalModelName, setExternalModelName] = useState("");
  const [serialKeyInjectType, setSerialKeyInjectType] = useState<string>("");
  const [vanType, setVanType] = useState<string>("");
  const [terminalColor, setTerminalColor] = useState("WHITE");
  const [loading, setLoading] = useState(false);
  const [injectTypeOptions, setInjectTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [vanTypeOptions, setVanTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const isValid =
    internalModelName.trim() !== "" &&
    externalModelName.trim() !== "" &&
    serialKeyInjectType !== "" &&
    vanType !== "" &&
    terminalColor !== "";

  useEffect(() => {
    Promise.all([
      axiosInstance.get("/api/v1/common/serial-key-inject-type"),
      axiosInstance.get("/api/v1/common/van"),
    ])
      .then(([injectRes, vanRes]) => {
        if (
          injectRes.data?.code === "200" &&
          Array.isArray(injectRes.data.data)
        ) {
          const opts = injectRes.data.data.map(
            (item: { code: string; name: string }) => ({
              value: item.code,
              label: `${item.code}: ${item.name}`,
            }),
          );
          setInjectTypeOptions(opts);
          if (opts.length > 0) {
            setSerialKeyInjectType(opts[0].value);
          }
        }
        if (vanRes.data?.code === "200" && Array.isArray(vanRes.data.data)) {
          const opts = vanRes.data.data.map(
            (item: { code: string; name: string }) => ({
              value: item.code,
              label: item.name,
            }),
          );
          setVanTypeOptions(opts);
        }
      })
      .catch(() => {
        toast.error("공통 코드 조회에 실패하였습니다.");
      });
  }, []);

  const handleRegister = async () => {
    if (!isValid) {
      toast.error("필수 항목을 모두 입력하세요.");
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/v1/terminal/model", {
        internalModelName: normalizeModelName(internalModelName),
        externalModelName: normalizeModelName(externalModelName),
        serialKeyInjectType: Number(serialKeyInjectType),
        vanType: Number(vanType),
        terminalColor,
      });
      if (res.data?.code === "200" || res.status === 202) {
        await setActionLog({
          eventType: "CREATE",
          title: "단말기 모델 등록 성공",
          content: {
            internalModelName,
            externalModelName,
            serialKeyInjectType,
            vanType,
          },
        });
        onConfirm?.(true);
      } else {
        toast.error(res.data?.msg || "등록에 실패하였습니다.");
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { msg?: string } } })?.response?.data
          ?.msg || "등록에 실패하였습니다.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title="단말기 모델 등록"
      closeOnClickOutside={false}
    >
      <TextInput
        label="내부모델"
        mt="md"
        size="lg"
        placeholder="예: SPC-402"
        withAsterisk
        value={internalModelName}
        onChange={(e) =>
          setInternalModelName(normalizeModelName(e.currentTarget.value))
        }
        disabled={loading}
      />
      <TextInput
        label="외부모델"
        mt="md"
        size="lg"
        placeholder="예: SCS-4200"
        withAsterisk
        value={externalModelName}
        onChange={(e) =>
          setExternalModelName(normalizeModelName(e.currentTarget.value))
        }
        disabled={loading}
      />
      <Select
        label="시리얼 키 주입 타입"
        mt="md"
        size="lg"
        withAsterisk
        data={injectTypeOptions}
        value={serialKeyInjectType || null}
        onChange={(v) => setSerialKeyInjectType(v ?? "")}
        allowDeselect={false}
        disabled={loading}
      />
      <Select
        label="VAN 타입"
        mt="md"
        size="lg"
        withAsterisk
        placeholder="선택"
        data={vanTypeOptions}
        value={vanType || null}
        onChange={(v) => setVanType(v ?? "")}
        allowDeselect={false}
        disabled={loading}
      />
      <Select
        label="색상"
        mt="md"
        size="lg"
        withAsterisk
        data={[
          { value: "BLACK", label: "BLACK" },
          { value: "WHITE", label: "WHITE" },
        ]}
        value={terminalColor || null}
        onChange={(v) => setTerminalColor(v ?? "WHITE")}
        allowDeselect={false}
        disabled={loading}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        size="lg"
        color="#222b45"
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleRegister}
        disabled={!isValid || loading}
      >
        등록
      </Button>
    </Modal>
  );
}

export function TerminalModelUpdate({
  onConfirm,
  onCancel,
  row,
}: TerminalModelUpdateProps) {
  const [useYn, setUseYn] = useState(row.useYn || "Y");
  const [loading, setLoading] = useState(false);

  const isChanged = useYn !== (row.useYn || "Y");

  const handleUpdate = async () => {
    if (useYn !== "Y" && useYn !== "N") {
      toast.error("사용 여부는 Y 또는 N만 선택할 수 있습니다.");
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.patch("/api/v1/terminal/model", {
        terminalModelId: row.terminalModelId,
        useYn,
      });
      if (res.data?.code === "200") {
        await setActionLog({
          eventType: "UPDATE",
          title: "단말기 모델 수정 성공",
          content: { terminalModelId: row.terminalModelId, useYn },
        });
        onConfirm?.(true);
      } else {
        toast.error(res.data?.msg || "수정에 실패하였습니다.");
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { msg?: string } } })?.response?.data
          ?.msg || "수정에 실패하였습니다.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title="단말기 모델 수정"
      closeOnClickOutside={false}
    >
      <Text size="sm" c="dimmed" mt="md">
        내부모델/외부모델과 주입 타입은 변경할 수 없습니다. 사용 여부만
        수정됩니다.
      </Text>
      <TextInput
        label="내부모델"
        mt="md"
        size="lg"
        value={row.internalModelName}
        disabled
      />
      <TextInput
        label="외부모델"
        mt="md"
        size="lg"
        value={row.externalModelName}
        disabled
      />
      <Select
        label="사용 여부"
        mt="md"
        size="lg"
        withAsterisk
        data={[
          { value: "Y", label: "Y (사용)" },
          { value: "N", label: "N (미사용)" },
        ]}
        value={useYn}
        onChange={(v) => setUseYn(v ?? "Y")}
        allowDeselect={false}
        disabled={loading}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        size="lg"
        color="#222b45"
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUpdate}
        disabled={!isChanged || loading}
      >
        수정
      </Button>
    </Modal>
  );
}

export function UserUpdate({
  onConfirm,
  onCancel,
  row,
  workerYn,
}: UserUpdateProps) {
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const [roleCodeOptions, setRoleCodeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [roleCode, setRoleCode] = useState("");
  const [userNameError, setUserNameError] = useState<string | null>(null);
  const userTypeName = workerYn === "Y" ? "작업자" : "유저";

  useEffect(() => {
    axiosInstance
      .get("/api/v1/role/permission/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          workerYn: workerYn,
        },
      })
      .then((res) => {
        const mapped = res.data.data.list.map((item: any) => ({
          value: item.roles,
          label: item.roleName,
        }));
        setRoleCodeOptions(mapped);
      })
      .catch((err) => {
        console.log(err);
        setRoleCodeOptions([]);
      });

    setUserName(row.userName);
    setRoleCode(row.roles || "");
  }, []);

  useEffect(() => {
    if (userName === row.userName && roleCode === row.roles) {
      setIsValidate(false);
    } else {
      setIsValidate(true);
    }
  }, [userName, roleCode]);

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserName(value);

    if (value.length < 2) {
      setUserNameError("이름은 최소 2글자 이상이여야합니다.");
    } else {
      setUserNameError(null);
    }
  };

  const handleUserUpdate = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.patch("/api/v1/user", {
        userNo: row.userNo,
        userId: row.userId,
        userName: userName,
        roleCode: roleCode,
      });
      if (res.data.code === "200") {
        onConfirm?.(true);
        await setActionLog({
          eventType: "UPDATE",
          title: `${userTypeName}수정 성공`,
          content: JSON.stringify({
            userNo: row.userNo,
            userId: row.userId,
            userName,
            roleCode,
          }),
        });
      } else {
        toast.error(res.data.msg || `${userTypeName}수정 실패`);
        await setActionLog({
          eventType: "UPDATE",
          title: `${userTypeName}수정 실패`,
          content: JSON.stringify({
            userNo: row.userNo,
            userId: row.userId,
            userName,
            roleCode,
          }),
        });
      }
    } catch (err) {
      toast.error(`${userTypeName}수정 중 오류 발생`);
      await setActionLog({
        eventType: "UPDATE",
        title: `${userTypeName}수정 실패`,
        content: err,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={`${userTypeName} 수정`}
      closeOnClickOutside={false}
    >
      <TextInput
        label="아이디"
        mt="md"
        size="lg"
        placeholder="아이디"
        withAsterisk
        value={row.userId}
        disabled={true}
      />
      <TextInput
        label="이름"
        mt="md"
        size="lg"
        placeholder="이름"
        withAsterisk
        value={userName}
        onChange={handleUserName}
        disabled={loading}
        error={userNameError}
      />
      <Select
        label="권한"
        mt="md"
        size="lg"
        withAsterisk
        data={roleCodeOptions}
        defaultValue={roleCode}
        onChange={(_value, option) => setRoleCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        size="lg"
        color="#222b45"
        leftSection={<IconUserCircle size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserUpdate}
        disabled={!isValidate}
      >
        수정
      </Button>
    </Modal>
  );
}

export function RoleAdd({ onConfirm, onCancel }: RoleAddProps) {
  type WorkerYn = "Y" | "N";
  const [resourceList, setResourceList] = useState<
    { resourcesPermissionId: string; url: string; urlName: string }[]
  >([]);
  const [roles, setRoles] = useState("");
  const [roleName, setRoleName] = useState("");
  const [workerYn, setWorkerYn] = useState<WorkerYn>("N");
  const [isLoading, setIsLoading] = useState(true);

  const [checkedStates, setCheckedStates] = useState<{
    [id: number]: { read: boolean; write: boolean };
  }>({});
  const [initialStates, setInitialStates] = useState<{
    [id: number]: { read: boolean; write: boolean };
  }>({});

  useEffect(() => {
    setting();
  }, []);

  const setting = async () => {
    try {
      const r_res = await axiosInstance.get("/api/v1/role/resource");
      const r = r_res.data.data;
      setResourceList(r);

      if (r.length > 0) {
        const init: { [id: number]: { read: boolean; write: boolean } } = {};
        r.forEach(
          (item: {
            resourcesPermissionId: string;
            url: string;
            urlName: string;
          }) => {
            init[Number(item.resourcesPermissionId)] = {
              read: false,
              write: false,
            };
          },
        );
        setCheckedStates(init);
        setInitialStates(init);
      }
    } catch (err) {
      setResourceList([]);
      setCheckedStates({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCheckedStates(JSON.parse(JSON.stringify(initialStates)));
    setRoles("");
    setRoleName("");
  };

  const handleUpdate = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const resParam = {
        roles: roles,
        rolesName: roleName,
        workerYn: workerYn,
        role_permissions: resourceList.map((res) => {
          const state = checkedStates[Number(res.resourcesPermissionId)] || {
            read: false,
            write: false,
          };
          return {
            resourcesPermissionId: Number(res.resourcesPermissionId),
            isRead: state.read ? "Y" : "N",
            isWrite: state.write ? "Y" : "N",
          };
        }),
      };
      const res = await axiosInstance.post("/api/v1/role/permission", resParam);
      if (res.data.code !== "200") throw new Error("권한등록 실패");
      await setActionLog({
        eventType: "CREATE",
        title: "권한등록 성공",
        content: resParam,
      });
      onConfirm?.(true);
    } catch (err: any) {
      await setActionLog({
        eventType: "CREATE",
        title: "권한등록 실패",
        content: err,
      });
      toast.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const rows = resourceList.map((row) => {
    const state = checkedStates[Number(row.resourcesPermissionId)] || {
      read: false,
      write: false,
    };

    const handleRowToggle = () => {
      const newValue = !(state.read && state.write);
      // ✅ read & write 둘 다 true면 → false로, 하나라도 false면 → 둘 다 true로

      setCheckedStates((prev) => ({
        ...prev,
        [Number(row.resourcesPermissionId)]: {
          read: newValue,
          write: newValue,
        },
      }));
    };

    return (
      <Table.Tr key={row.resourcesPermissionId}>
        <Table.Td
          style={{ color: "blue", cursor: "pointer" }} // 링크처럼 보이게
          onClick={handleRowToggle}
        >
          {row.urlName}
        </Table.Td>
        <Table.Td
          style={{ color: "blue", cursor: "pointer" }} // 링크처럼 보이게
          onClick={handleRowToggle}
        >
          {row.url}
        </Table.Td>
        <Table.Td>
          <Checkbox
            checked={state.read}
            onChange={(e) =>
              setCheckedStates((prev) => ({
                ...prev,
                [Number(row.resourcesPermissionId)]: {
                  ...state,
                  read: e.target.checked,
                },
              }))
            }
          />
        </Table.Td>
        <Table.Td>
          <Checkbox
            checked={state.write}
            onChange={(e) =>
              setCheckedStates((prev) => ({
                ...prev,
                [Number(row.resourcesPermissionId)]: {
                  ...state,
                  write: e.target.checked,
                },
              }))
            }
          />
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Modal size={"70%"} opened={true} onClose={onCancel} title={"권한 등록"}>
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: "sm", bg: "rgba(0,0,0,0.3)" }}
        loaderProps={{ color: "pink", type: "bars" }}
      />
      <Grid>
        <Grid.Col span={4}>
          <Flex justify={"space-between"} pb={"1rem"}>
            <Button
              variant="default"
              leftSection={<IconRefresh stroke={1.5} />}
              onClick={() => {
                handleReset();
              }}
              disabled={isLoading}
            >
              되돌리기
            </Button>
            <Button
              leftSection={<IconCheck stroke={1.5} />}
              variant="gradient"
              gradient={{ from: "#e0eafc", to: "#b6e0fe", deg: 180 }}
              c={"#141414"}
              onClick={() => {
                handleUpdate();
              }}
              disabled={isLoading}
            >
              등록
            </Button>
          </Flex>
          <TextInput
            pb={8}
            label="권한 ID"
            description="예시) ROLE_EXAMPLE"
            placeholder="권한 ID"
            withAsterisk
            value={roles}
            onChange={(e) => {
              setRoles(e.target.value);
            }}
          />
          <TextInput
            pb={8}
            label="권한 이름"
            description="예시) 테스트 사용자"
            placeholder="권한 이름"
            withAsterisk
            value={roleName}
            onChange={(e) => {
              setRoleName(e.target.value);
            }}
          />
          <Flex justify={"flex-end"} pb={8}>
            <Checkbox
              label="작업자 전용권한"
              checked={workerYn === "Y"}
              onChange={(e) => {
                setWorkerYn(e.target.checked ? "Y" : "N");
              }}
            />
          </Flex>
        </Grid.Col>
        <Grid.Col span="auto">
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>메뉴</Table.Th>
                <Table.Th>URL</Table.Th>
                <Table.Th>조회</Table.Th>
                <Table.Th>실행</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Grid.Col>
      </Grid>
    </Modal>
  );
}

export function RoleUpdate({ onConfirm, onCancel, row }: RoleUpdateProps) {
  type WorkerYn = "Y" | "N";
  const [resourceList, setResourceList] = useState<
    { resourcesPermissionId: string; url: string; urlName: string }[]
  >([]);
  const [roles, setRoles] = useState("");
  const [roleName, setRoleName] = useState("");
  const [workerYn, setWorkerYn] = useState<WorkerYn>("N");
  const [roleInfo, setRoleInfo] = useState<RoleList | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [checkedStates, setCheckedStates] = useState<{
    [id: number]: { read: boolean; write: boolean };
  }>({});
  const [initialStates, setInitialStates] = useState<{
    [id: number]: { read: boolean; write: boolean };
  }>({});

  useEffect(() => {
    setting();
  }, []);

  const setting = async () => {
    console.log(row);
    try {
      const r_res = await axiosInstance.get("/api/v1/role/resource");
      const r = r_res.data.data;
      setResourceList(r);
      setRoleInfo(row);
      setRoles(row.roles);
      setRoleName(row.roleName);
      setWorkerYn(row.workerYn);

      if (r.length > 0) {
        const init: { [id: number]: { read: boolean; write: boolean } } = {};
        r.forEach(
          (item: {
            resourcesPermissionId: string;
            url: string;
            urlName: string;
          }) => {
            const perm = row.permissions.find(
              (pItem: RoleListPermissions) =>
                pItem.resourcesPermissionId ===
                Number(item.resourcesPermissionId),
            );
            init[Number(item.resourcesPermissionId)] = {
              read: perm?.isRead === "Y",
              write: perm?.isWrite === "Y",
            };
          },
        );
        setCheckedStates(init);
        setInitialStates(init);
      }
    } catch (err) {
      console.log(err);
      setResourceList([]);
      setRoleInfo(null);
      setCheckedStates({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCheckedStates(JSON.parse(JSON.stringify(initialStates)));
    setRoleName(roleInfo?.roleName || "");
    setWorkerYn(roleInfo?.workerYn || "N");
  };

  const handleUpdate = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const resParams = {
        roles: row.roles,
        rolesName: roleName,
        workerYn: workerYn,
        role_permissions: resourceList.map((res) => {
          const state = checkedStates[Number(res.resourcesPermissionId)] || {
            read: false,
            write: false,
          };
          return {
            resourcesPermissionId: Number(res.resourcesPermissionId),
            isRead: state.read ? "Y" : "N",
            isWrite: state.write ? "Y" : "N",
          };
        }),
      };
      const res = await axiosInstance.post(
        "/api/v1/role/permission",
        resParams,
      );
      if (res.data.code !== "200") throw new Error("권한수정 실패");
      await setActionLog({
        eventType: "UPDATE",
        title: "권한수정 성공",
        content: resParams,
      });
      onConfirm?.(true);
    } catch (err: any) {
      await setActionLog({
        eventType: "UPDATE",
        title: "권한수정 실패",
        content: err,
      });
      toast.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const rows = resourceList.map((row) => {
    const state = checkedStates[Number(row.resourcesPermissionId)] || {
      read: false,
      write: false,
    };

    const handleRowToggle = () => {
      const newValue = !(state.read && state.write);
      // ✅ read & write 둘 다 true면 → false로, 하나라도 false면 → 둘 다 true로

      setCheckedStates((prev) => ({
        ...prev,
        [Number(row.resourcesPermissionId)]: {
          read: newValue,
          write: newValue,
        },
      }));
    };

    return (
      <Table.Tr key={row.resourcesPermissionId}>
        <Table.Td
          style={{ color: "blue", cursor: "pointer" }} // 링크처럼 보이게
          onClick={handleRowToggle}
        >
          {row.urlName}
        </Table.Td>
        <Table.Td
          style={{ color: "blue", cursor: "pointer" }} // 링크처럼 보이게
          onClick={handleRowToggle}
        >
          {row.url}
        </Table.Td>
        <Table.Td>
          <Checkbox
            checked={state.read}
            onChange={(e) =>
              setCheckedStates((prev) => ({
                ...prev,
                [Number(row.resourcesPermissionId)]: {
                  ...state,
                  read: e.target.checked,
                },
              }))
            }
          />
        </Table.Td>
        <Table.Td>
          <Checkbox
            checked={state.write}
            onChange={(e) =>
              setCheckedStates((prev) => ({
                ...prev,
                [Number(row.resourcesPermissionId)]: {
                  ...state,
                  write: e.target.checked,
                },
              }))
            }
          />
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Modal size={"70%"} opened={true} onClose={onCancel} title={"권한 수정"}>
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: "sm", bg: "rgba(0,0,0,0.3)" }}
        loaderProps={{ color: "pink", type: "bars" }}
      />
      <Grid>
        <Grid.Col span={4}>
          <Flex justify={"space-between"} pt={"1rem"}>
            <Button
              variant="default"
              leftSection={<IconRefresh stroke={1.5} />}
              onClick={() => {
                handleReset();
              }}
              disabled={isLoading}
            >
              되돌리기
            </Button>
            <Button
              leftSection={<IconCheck stroke={1.5} />}
              variant="gradient"
              gradient={{ from: "#e0eafc", to: "#b6e0fe", deg: 180 }}
              c={"#141414"}
              onClick={() => {
                handleUpdate();
              }}
              disabled={isLoading}
            >
              수정
            </Button>
          </Flex>
          <TextInput
            pb={8}
            label="권한 ID"
            placeholder="ex) ROLE_EXAMPLE"
            value={roles}
            disabled={true}
          />
          <TextInput
            pb={8}
            label="권한 이름"
            description="예시) 테스트 사용자"
            placeholder="권한 이름"
            withAsterisk
            value={roleName}
            onChange={(e) => {
              setRoleName(e.target.value);
            }}
          />
          <Flex justify={"flex-end"} pb={8}>
            <Checkbox
              label="작업자 전용권한"
              checked={workerYn === "Y"}
              onChange={(e) => {
                setWorkerYn(e.target.checked ? "Y" : "N");
              }}
            />
          </Flex>
        </Grid.Col>
        <Grid.Col span="auto">
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>메뉴</Table.Th>
                <Table.Th>URL</Table.Th>
                <Table.Th>조회</Table.Th>
                <Table.Th>실행</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Grid.Col>
      </Grid>
    </Modal>
  );
}

export function PasswordChange({ onConfirm, onCancel }: PasswordChangeProps) {
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");

  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const [password2Error, setPassword2Error] = useState<string | null>(null);

  useEffect(() => {
    if (oldPassword && password && password2 && password === password2) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [oldPassword, password, password2]);

  useEffect(() => {
    if (password === password2) {
      setPassword2Error(null);
    } else {
      setPassword2Error("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
    }
  }, [password2]);

  const handleUserRegist = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.patch("/api/v1/user/pw", {
        currentPassword: oldPassword,
        newPassword: password,
      });
      if (res.data.code === "200") {
        await setActionLog({
          eventType: "UPDATE",
          title: "비밀번호 변경 성공",
        });
        onConfirm?.(true);
      } else {
        toast.error("비밀번호 변경 실패");
        await setActionLog({
          eventType: "UPDATE",
          title: "비밀번호 변경 실패",
        });
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "UPDATE",
        title: "비밀번호 변경 실패",
        content: err,
      });
      toast.error("비밀번호 변경 실패");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"비밀번호 변경"}
      closeOnClickOutside={false}
    >
      <PasswordInput
        label="기존 비밀번호"
        mt="md"
        size="lg"
        placeholder="기존 비밀번호"
        withAsterisk
        value={oldPassword}
        onChange={(e) => {
          setOldPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="신규 비밀번호"
        mt="md"
        size="lg"
        placeholder="신규 비밀번호"
        withAsterisk
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="신규 비밀번호 확인"
        mt="md"
        size="lg"
        placeholder="신규 비밀번호 확인"
        withAsterisk
        value={password2}
        onChange={(e) => {
          setPassword2(e.target.value);
        }}
        disabled={loading}
        error={password2Error}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        size="lg"
        color="#222b45"
        leftSection={<IconLock size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserRegist}
        disabled={!isValidate}
      >
        비밀번호 변경
      </Button>
    </Modal>
  );
}

export function TerminalView({ onConfirm, onCancel, row }: TerminalViewProps) {
  const [vanMap, setVanMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const injectLabel = (yn: string) => (yn === "Y" ? "주입" : "미주입");

  useEffect(() => {
    void getData();
  }, []);

  const getData = async () => {
    setLoading(true);
    try {
      const vanRes = await axiosInstance.get("/api/v1/common/van");
      const vanData: Record<string, string> = {};
      (vanRes.data?.data ?? []).forEach(
        (item: { code: string; name: string }) => {
          vanData[item.code] = item.name;
        },
      );
      setVanMap(vanData);
    } catch {
      setVanMap({});
    } finally {
      setLoading(false);
    }
  };

  const thWidth = 160;
  const info = (
    <Table
      variant="vertical"
      layout="fixed"
      withTableBorder
      withColumnBorders
      mb={"1rem"}
      fz={"1.125rem"}
    >
      <Table.Tbody>
        <Table.Tr>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            S/N
          </Table.Th>
          <Table.Td bg={"#FFF"}>{row.spSerialNo || "-"}</Table.Td>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            VAN
          </Table.Th>
          <Table.Td bg={"#FFF"}>
            {vanMap[row.vanType] ?? row.vanType ?? "-"}
          </Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            내부모델
          </Table.Th>
          <Table.Td bg={"#FFF"}>{row.internalModelName || "-"}</Table.Td>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            외부모델
          </Table.Th>
          <Table.Td bg={"#FFF"}>{row.externalModelName || "-"}</Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            색상
          </Table.Th>
          <Table.Td bg={"#FFF"} colSpan={3}>
            {row.terminalColor || "-"}
          </Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            MAC 주소
          </Table.Th>
          <Table.Td bg={"#FFF"} colSpan={3}>
            {row.macAddress || "-"}
          </Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            S/N 주입
          </Table.Th>
          <Table.Td bg={"#FFF"}>
            {injectLabel(row.spSerialInjectionYn)}
          </Table.Td>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            MAC 주입
          </Table.Th>
          <Table.Td bg={"#FFF"}>{injectLabel(row.macInjectionYn)}</Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );

  return (
    <Modal opened={true} onClose={onCancel} title={"단말기 상세"} size={"100%"}>
      {loading ? (
        <Text p={12} c="dimmed">
          조회 중...
        </Text>
      ) : (
        info
      )}
      <Flex justify={"flex-end"} mt={"1rem"}>
        <Button
          leftSection={<IconX size={18} stroke={1.5} />}
          onClick={() => {
            onConfirm?.(false);
          }}
          variant="default"
        >
          닫기
        </Button>
      </Flex>
    </Modal>
  );
}

export function ActionLogDetail({
  onConfirm,
  onCancel,
  row,
}: ActionLogDetailProps) {
  return (
    <Modal opened={true} onClose={onCancel} title={"로그 상세"} size={"lg"}>
      <ScrollArea h={"60vh"} pos={"relative"} bg={"#fff"} bdrs={8}>
        <Table>
          <Table.Thead>
            <Table.Tr bg={"#f5f6fa"} ta={"left"} pos={"sticky"} top={0}>
              <Table.Th p={12} w={120} pos={"sticky"} top={0}>
                항목
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                내용
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                일시
              </Table.Td>
              <Table.Td p={"8px 12px"}>
                {boardDateFormat(row.createdDate)}
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                분류
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.eventType}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                메뉴
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.menuUrl}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                사용자
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.userName}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                안드로이드 S/N
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.serialNo || "-"}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                키워드
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.keyword || "-"}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                행동
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.action}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                IP
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.ip}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td
                p={"8px 12px"}
                fw={600}
                bg={"#f8f9fa"}
                style={{ verticalAlign: "top" }}
              >
                User Agent
              </Table.Td>
              <Table.Td p={"8px 12px"}>
                <Text
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: "0.8rem",
                    lineHeight: "1.3",
                  }}
                >
                  {row.userAgent}
                </Text>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td
                p={"8px 12px"}
                fw={600}
                bg={"#f8f9fa"}
                style={{ verticalAlign: "top" }}
              >
                내용
              </Table.Td>
              <Table.Td p={"8px 12px"}>
                <Text
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                    fontSize: "0.9rem",
                    lineHeight: "1.4",
                  }}
                >
                  {row.message}
                </Text>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </ScrollArea>
      <Flex justify={"flex-end"} mt={"1rem"}>
        <Button
          leftSection={<IconX size={18} stroke={1.5} />}
          onClick={() => {
            onConfirm?.(false);
          }}
          variant="default"
        >
          닫기
        </Button>
      </Flex>
    </Modal>
  );
}

export function TerminalDelete({ onConfirm, onCancel }: TerminalDeleteProps) {
  const [startId, setStartId] = useState("");
  const [endId, setEndId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<any[]>([]);
  const [canDelete, setCanDelete] = useState(false);
  const theme = useMantineTheme();

  const handleCheck = async () => {
    if (!startId || !endId) {
      setError("번호를 입력해주세요.");
      return;
    }

    setError(null);
    setInfoMessage(null);
    setChecking(true);

    try {
      // 삭제 가능 여부 확인 API 호출 (GET, params)
      const response = await axiosInstance.get(
        "/api/v1/terminal/delete/check",
        {
          params: {
            startAndroidSn: startId,
            endAndroidSn: endId,
          },
        },
      );

      if (response.data.code === "200") {
        const data = response.data.data ?? {};
        const terminals = Array.isArray(data.terminals) ? data.terminals : [];
        const totalCount = Number(data.totalCount ?? terminals.length ?? 0);
        const deletable = data.deletable === true;
        setCheckResult(terminals);
        setInfoMessage(data.message || null);
        setCanDelete(totalCount > 0 && deletable);
        if (totalCount === 0) {
          setError("삭제할 단말기가 없습니다.");
        } else if (!deletable) {
          setError(
            data.message ||
              "주입된 단말기가 있어 삭제 할 수 없습니다. 관리자에게 문의하십시요",
          );
          setInfoMessage(null);
        }
      } else {
        setError(response.data.msg || "확인 중 오류가 발생했습니다.");
        setCanDelete(false);
        setCheckResult([]);
        setInfoMessage(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.msg || "확인 중 오류가 발생했습니다.");
      setCanDelete(false);
      setCheckResult([]);
      setInfoMessage(null);
    } finally {
      setChecking(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) {
      setError("먼저 확인 버튼을 눌러주세요.");
      return;
    }

    const confirmMessage =
      infoMessage || "삭제할 단말기가 있습니다. 정말 삭제하시겠습니까?";
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get("/api/v1/terminal/delete", {
        params: {
          startAndroidSn: startId,
          endAndroidSn: endId,
        },
      });

      if (response.data.code === "200") {
        await setActionLog({
          eventType: "DELETE",
          title: "단말기 일괄 삭제 성공",
          content: `S/N: ${startId}~${endId}`,
        });
        if ((response.data.data?.deletedCount ?? 0) === 0) {
          toast.info("삭제할 단말기가 없습니다.");
        } else {
          toast.success("삭제가 완료되었습니다.");
        }
        onConfirm?.(true);
      } else {
        setError(response.data.msg || "삭제 중 오류가 발생했습니다.");
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "DELETE",
        title: "단말기 일괄 삭제 실패",
        content: err,
      });
      setError(err.response?.data?.msg || "삭제 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={true} onClose={onCancel} title="단말기 삭제" size="lg">
      <Text size="sm" mb="md" c="dimmed">
        삭제할 단말기의 S/N 범위를 입력하세요.
      </Text>

      <Flex gap="sm" align="flex-end">
        <TextInput
          label="시작 S/N"
          placeholder="시작 S/N"
          value={startId}
          onChange={(e) => {
            setStartId(e.target.value.toUpperCase());
            setCanDelete(false);
            setInfoMessage(null);
            setError(null);
          }}
          style={{ flex: 1 }}
        />
        <Text style={{ paddingBottom: "0.5rem" }}>~</Text>
        <TextInput
          label="끝 S/N"
          placeholder="끝 S/N"
          value={endId}
          onChange={(e) => {
            setEndId(e.target.value.toUpperCase());
            setCanDelete(false);
            setInfoMessage(null);
            setError(null);
          }}
          style={{ flex: 1 }}
        />
        <Button
          onClick={handleCheck}
          disabled={checking || loading}
          loading={checking}
        >
          확인
        </Button>
      </Flex>

      {error && (
        <Text size="sm" c="red" mt="xs" fw={600}>
          {error}
        </Text>
      )}

      {!error && infoMessage && (
        <Text size="sm" c={canDelete ? "blue" : "dimmed"} mt="xs">
          {infoMessage}
        </Text>
      )}

      {checkResult.length > 0 && (
        <div style={{ marginTop: "1rem" }}>
          <Text size="sm" fw={600} mb="xs">
            확인 결과 ({checkResult.length}개)
          </Text>
          <ScrollArea h={200}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>번호</Table.Th>
                  <Table.Th>S/N</Table.Th>
                  <Table.Th>S/N 주입</Table.Th>
                  <Table.Th>MAC 주입</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {checkResult.map((item) => {
                  const hasInjected =
                    item.spSerialInjectionYn === "Y" ||
                    item.macInjectionYn === "Y";
                  return (
                    <Table.Tr
                      key={item.terminalId}
                      bg={hasInjected ? "#ffe6e6" : undefined}
                    >
                      <Table.Td>{item.terminalId}</Table.Td>
                      <Table.Td>{item.spSerialNo || "-"}</Table.Td>
                      <Table.Td>
                        {item.spSerialInjectionYn === "Y" ? "주입" : "미주입"}
                      </Table.Td>
                      <Table.Td>
                        {item.macInjectionYn === "Y" ? "주입" : "미주입"}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </div>
      )}

      <Flex justify="space-between" mt="lg">
        <Button
          variant="default"
          onClick={onCancel}
          leftSection={<IconX size={18} />}
        >
          취소
        </Button>
        <Button
          onClick={handleDelete}
          loading={loading}
          disabled={!canDelete}
          leftSection={<IconX size={18} />}
          color="red"
        >
          삭제
        </Button>
      </Flex>
    </Modal>
  );
}

export function TerminalIncoming({
  onConfirm,
  onCancel,
}: TerminalIncomingProps) {
  const [startId, setStartId] = useState("");
  const [endId, setEndId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<any[]>([]);
  const [canDelete, setCanDelete] = useState(false);
  const theme = useMantineTheme();

  const todayStr = dayjs().format("YYYY-MM-DD"); // KST 기준 오늘 날짜
  const lastMonthFirstDay = dayjs()
    .subtract(1, "month") // 저번 달로 이동
    .startOf("month") // 그 달의 첫째 날
    .format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();

  const handleCheck = async () => {
    if (!startId || !endId) {
      setError("S/N 혹은 날짜를 입력해주세요.");
      return;
    }

    setError(null);
    setChecking(true);

    try {
      // 출고 가능 여부 확인 API 호출 (GET, params)
      const response = await axiosInstance.get(
        "/api/v1/terminal/incoming/check",
        {
          params: {
            startSerialNo: startId,
            endSerialNo: endId,
            startDate: startDate,
            endDate: endDate,
          },
        },
      );

      if (response.data.code === "200") {
        setCheckResult(response.data.data);
        setCanDelete(true);
      } else {
        setError(response.data.msg || "확인 중 오류가 발생했습니다.");
        setCanDelete(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.msg || "확인 중 오류가 발생했습니다.");
      setCanDelete(false);
    } finally {
      setChecking(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) {
      setError("먼저 확인 버튼을 눌러주세요.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const incomingRequest = {
        startSerialNo: startId,
        endSerialNo: endId,
        startDate: startDate,
        endDate: endDate,
      };

      const response = await axiosInstance.post(
        "/api/v1/terminal/incoming",
        incomingRequest,
      );

      if (response.data.code === "200") {
        await setActionLog({
          eventType: "DELETE",
          title: "단말기 일괄 출고 성공",
          content: `Android S/N: ${startId}~${endId}`,
        });
        toast.success("출고가 완료되었습니다.");
        onConfirm?.(true);
      } else {
        setError(response.data.msg || "출고 중 오류가 발생했습니다.");
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "DELETE",
        title: "단말기 일괄 출고 실패",
        content: err,
      });
      setError(err.response?.data?.msg || "출고 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={true} onClose={onCancel} title="단말기 출고" size="lg">
      <Text size="sm" mb="md" c="dimmed">
        삭제할 단말기의 안드로이드 S/N 범위를 입력하세요.
      </Text>

      <Flex gap="sm" align="flex-end" mb="md">
        <TextInput
          label="시작 번호"
          placeholder="시작 번호"
          value={startId}
          onChange={(e) => {
            setStartId(e.target.value);
            setCanDelete(false);
          }}
          style={{ flex: 1 }}
        />
        {/* <Text style={{ paddingBottom: "0.5rem" }}>~</Text> */}
        <TextInput
          label="끝 번호"
          placeholder="끝 번호"
          value={endId}
          onChange={(e) => {
            setEndId(e.target.value);
            setCanDelete(false);
          }}
          style={{ flex: 1 }}
        />
      </Flex>

      <Flex gap="sm" align="flex-end" mb="md">
        <DateInput
          label="시작 날짜"
          placeholder="날짜를 선택하세요"
          valueFormat="YYYY-MM-DD"
          value={startDate}
          onChange={(val) => {
            setStartDate(val || undefined);
          }}
          nextIcon={<IconChevronRight size={16} />}
          previousIcon={<IconChevronLeft size={16} />}
          renderDay={(dateStr) => {
            const dateObj = new Date(dateStr); // 문자열 → Date 객체
            const day = dateObj.getDay();

            const style: React.CSSProperties = {
              color: day === 0 ? "red" : day === 6 ? "blue" : undefined,
            };

            return <div style={style}>{dateObj.getDate()}</div>;
          }}
          style={{ flex: 1 }}
        />
        <DateInput
          label="종료 날짜"
          placeholder="날짜를 선택하세요"
          valueFormat="YYYY-MM-DD"
          value={endDate}
          onChange={(val) => {
            let newEnd = val || todayStr;
            if (startDate && new Date(newEnd) < new Date(startDate)) {
              newEnd = startDate;
            }
            setEndDate(newEnd);
          }}
          nextIcon={<IconChevronRight size={16} />}
          previousIcon={<IconChevronLeft size={16} />}
          renderDay={(dateStr) => {
            const dateObj = new Date(dateStr); // 문자열 → Date 객체
            const day = dateObj.getDay();

            const style: React.CSSProperties = {
              color: day === 0 ? "red" : day === 6 ? "blue" : undefined,
            };

            return <div style={style}>{dateObj.getDate()}</div>;
          }}
          style={{ flex: 1 }}
        />
      </Flex>

      <Flex gap="sm" align="flex-end" mb="md">
        <Button
          onClick={handleCheck}
          disabled={checking || loading}
          loading={checking}
        >
          확인
        </Button>
      </Flex>

      {error && (
        <Text size="sm" c="red" mt="xs">
          {error}
        </Text>
      )}

      {checkResult.length > 0 && (
        <div style={{ marginTop: "1rem" }}>
          <Text size="sm" fw={600} mb="xs">
            확인 결과 ({checkResult.length}개)
          </Text>
          <ScrollArea h={200}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>번호</Table.Th>
                  <Table.Th>안드로이드 S/N</Table.Th>
                  <Table.Th>N사마스터키</Table.Th>
                  <Table.Th>S/N</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {checkResult.map((item) => {
                  const hasInjected =
                    item.keyInjectionYn === "Y" ||
                    item.spSerialInjectionYn === "Y";
                  return (
                    <Table.Tr
                      key={item.terminalId}
                      bg={hasInjected ? "#ffe6e6" : undefined}
                    >
                      <Table.Td>{item.terminalId}</Table.Td>
                      <Table.Td>{item.serialNo}</Table.Td>
                      <Table.Td>
                        {item.keyInjectionYn === "Y" ? "주입" : "미주입"}
                      </Table.Td>
                      <Table.Td>
                        {item.spSerialInjectionYn === "Y" ? "주입" : "미주입"}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </div>
      )}

      <Flex justify="space-between" mt="lg">
        <Button
          variant="default"
          onClick={onCancel}
          leftSection={<IconX size={18} />}
        >
          취소
        </Button>
        <Button
          onClick={handleDelete}
          loading={loading}
          disabled={!canDelete}
          leftSection={<IconPackage size={18} />}
          color="green"
        >
          출고
        </Button>
      </Flex>
    </Modal>
  );
}

export function TerminalUpdate({ onConfirm, onCancel }: TerminalUpdateProps) {
  const [startId, setStartId] = useState("");
  const [endId, setEndId] = useState("");
  const [updateVan, setUpdateVan] = useState(false);
  const [updateModel, setUpdateModel] = useState(false);
  const [updateColor, setUpdateColor] = useState(false);
  const [vanType, setVanType] = useState("");
  const [model, setModel] = useState("");
  const [color, setColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vanTypeOptions, setVanTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [modelOptions, setModelOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const theme = useMantineTheme();

  useEffect(() => {
    // VAN 옵션 로드
    axiosInstance
      .get("/api/v1/common/van")
      .then((res) => {
        const mapped = res.data.data.map((item: any) => ({
          value: item.code,
          label: item.name,
        }));
        setVanTypeOptions(mapped);
      })
      .catch((err) => {
        console.log(err);
      });

    // 모델 옵션 로드
    axiosInstance
      .get("/api/v1/common/device")
      .then((res) => {
        const mapped = res.data.data.map((item: any) => ({
          value: item.code,
          label: item.name,
        }));
        setModelOptions(mapped);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const handleUpdate = async () => {
    const start = parseInt(startId);
    const end = parseInt(endId);

    if (!start || !end) {
      setError("번호를 입력해주세요.");
      return;
    }

    if (start > end) {
      setError("시작 번호가 끝 번호보다 큽니다.");
      return;
    }

    if (!updateVan && !updateModel && !updateColor) {
      setError("수정할 항목을 하나 이상 선택해주세요.");
      return;
    }

    if (updateVan && !vanType) {
      setError("VAN를 선택해주세요.");
      return;
    }

    if (updateModel && !model) {
      setError("모델을 선택해주세요.");
      return;
    }

    if (updateColor && !color) {
      setError("색상을 선택해주세요.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updateData: any = {
        startId: start,
        endId: end,
      };

      if (updateVan) {
        updateData.vanType = vanType;
      }
      if (updateModel) {
        updateData.deviceType = model;
      }
      if (updateColor) {
        updateData.deviceColor = color;
      }

      const response = await axiosInstance.patch(
        "/api/v1/terminal/bulk-update",
        updateData,
      );

      if (response.data.code === "200") {
        await setActionLog({
          eventType: "UPDATE",
          title: "단말기 일괄 수정 성공",
          content: JSON.stringify(updateData),
        });
        toast.success("수정이 완료되었습니다.");
        onConfirm?.(true);
      } else {
        setError(response.data.msg || "수정 중 오류가 발생했습니다.");
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "UPDATE",
        title: "단말기 일괄 수정 실패",
        content: err,
      });
      setError(err.response?.data?.msg || "수정 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={true} onClose={onCancel} title="단말기 수정" size="lg">
      <Text size="sm" mb="md" c="dimmed">
        수정할 단말기의 번호 범위를 입력하세요.
      </Text>

      <Flex gap="sm" align="flex-end" mb="md">
        <TextInput
          label="시작 번호"
          placeholder="시작 번호"
          value={startId}
          onChange={(e) => setStartId(e.target.value)}
          style={{ flex: 1 }}
        />
        <Text style={{ paddingBottom: "0.5rem" }}>~</Text>
        <TextInput
          label="끝 번호"
          placeholder="끝 번호"
          value={endId}
          onChange={(e) => setEndId(e.target.value)}
          style={{ flex: 1 }}
        />
      </Flex>

      <Text size="sm" fw={600} mb="xs">
        수정할 항목 선택
      </Text>

      <Checkbox
        label="VAN 변경"
        checked={updateVan}
        onChange={(e) => setUpdateVan(e.currentTarget.checked)}
        mb="sm"
      />

      {updateVan && (
        <Select
          label="VAN"
          placeholder="VAN를 선택하세요"
          data={vanTypeOptions}
          value={vanType}
          onChange={(value) => setVanType(value || "")}
          mb="md"
        />
      )}

      <Checkbox
        label="모델 변경"
        checked={updateModel}
        onChange={(e) => setUpdateModel(e.currentTarget.checked)}
        mb="sm"
      />

      {updateModel && (
        <Select
          label="모델"
          placeholder="모델을 선택하세요"
          data={modelOptions}
          value={model}
          onChange={(value) => setModel(value || "")}
          mb="md"
        />
      )}

      <Checkbox
        label="색상 변경"
        checked={updateColor}
        onChange={(e) => setUpdateColor(e.currentTarget.checked)}
        mb="sm"
      />

      {updateColor && (
        <Select
          label="색상"
          placeholder="색상을 선택하세요"
          data={[
            { value: "BLACK", label: "BLACK" },
            { value: "WHITE", label: "WHITE" },
          ]}
          value={color}
          onChange={(value) => setColor(value || "")}
          mb="md"
        />
      )}

      {error && (
        <Text size="sm" c="red" mb="md">
          {error}
        </Text>
      )}

      <Flex justify="space-between">
        <Button
          variant="default"
          onClick={onCancel}
          leftSection={<IconX size={18} />}
        >
          취소
        </Button>
        <Button
          onClick={handleUpdate}
          loading={loading}
          leftSection={<IconCheck size={18} />}
          variant="gradient"
          gradient={{ from: "#e0eafc", to: "#b6e0fe", deg: 180 }}
          c={"#141414"}
        >
          수정
        </Button>
      </Flex>
    </Modal>
  );
}
